/**
 * posMasterSync — pull POS-needed master data from Firestore to local Dexie.
 *
 * Runs when:
 *   - User is authenticated AND online
 *   - At app start (one-shot warm-up)
 *   - On reconnect after being offline
 *   - On manual user trigger ("Refresh data" button)
 *
 * Collections pulled (POS-only scope):
 *   customers           → customers table
 *   inventoryItems      → inventory_items table
 *   inventoryCategories → inventory_categories table
 *   accounts            → accounts table (full COA — small, ~200 docs typical)
 *   locations           → inventory_locations table
 *   banks               → banks table
 *   branches            → branches table
 *   financialYears      → financial_years table
 *
 * NOT pulled (don't matter for POS offline):
 *   vendors, employees, jobs, regions, areas, units, sub-categories — leave for later phases.
 *
 * Strategy: full re-fetch, throttled. POS needs hundreds-of-rows, not millions,
 * and full re-pull avoids the complexity of incremental sync (which requires
 * server-side tombstones for deletions). Since 2026-07-16 (Firebase cost) a
 * full pull runs at most once per 30 min per company per device — POS screen
 * remounts and reconnects inside that window reuse the existing Dexie snapshot.
 * The gate self-disables when the local cache is empty, and force:true always
 * syncs (manual "Refresh data" trigger).
 */

import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { cacheCollection } from './posLocalCache';
import { dexieDb } from '../dataStore/dexieSchema';
import { recordReads } from './readTelemetry';

// customers / inventoryItems / accounts are DELIBERATELY not here: since Tier 1
// (Firebase cost) the master-data cache (utils/masterDataStore.js) owns those
// three and writes the SAME Dexie tables (customers / inventory_items /
// accounts), keeping them fresh via a cheap change-check. Re-pulling them here
// every 30 min would just double the reads. POSScreen.loadData calls the cached
// getters on every open (online), so the tables are always populated before the
// device can go offline. This sync now handles only the POS-offline EXTRAS that
// the master cache does not cover.
const COLLECTIONS_TO_SYNC = [
  ['inventoryCategories', 'inventory_categories'],
  ['locations', 'inventory_locations'],
  ['banks', 'banks'],
  ['branches', 'branches'],
  ['financialYears', 'financial_years']
];

let _syncInFlight = false;
let _lastSyncAt = null;
const _listeners = new Set();

export const onSyncStatusChange = (cb) => {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
};

const notify = (status) => {
  for (const l of _listeners) { try { l(status); } catch { /* ignore */ } }
};

/**
 * Pull all POS-relevant master data from Firestore to Dexie.
 *
 * @param {string} companyId
 * @returns {Promise<{ ok: boolean, durationMs: number, counts: object, error?: Error }>}
 */
export const syncMasterDataToLocal = async (companyId, { force = false } = {}) => {
  if (!companyId) return { ok: false, durationMs: 0, counts: {}, error: new Error('No companyId') };
  if (_syncInFlight) return { ok: false, durationMs: 0, counts: {}, error: new Error('Sync already in progress') };

  // Throttle: skip the full re-pull if we synced this company < 30 min ago on
  // this device AND the local cache actually has data (empty cache = always
  // sync, so a cleared Dexie can never leave POS without masters).
  if (!force) {
    try {
      const gateKey = `amal_pos_sync_${companyId}`;
      const lastSync = parseInt(localStorage.getItem(gateKey), 10) || 0;
      if (Date.now() - lastSync < 30 * 60 * 1000) {
        // Probe a table THIS sync owns (financial_years — every company has ≥1
        // active FY) so an empty cache always forces a sync. inventory_items is
        // no longer synced here (owned by the master cache), so it can't be the
        // "did this sync run" signal any more.
        const fyCount = await dexieDb.financial_years.filter(r => r._companyId === companyId).count();
        if (fyCount > 0) {
          notify({ phase: 'skipped', reason: 'throttled', lastSyncAt: new Date(lastSync).toISOString() });
          return { ok: true, durationMs: 0, counts: {}, skipped: true };
        }
      }
    } catch { /* gate check failed — fall through to a normal sync */ }
  }

  _syncInFlight = true;
  notify({ phase: 'running', startedAt: new Date().toISOString() });
  const t0 = Date.now();
  const counts = {};

  try {
    // Parallel-fetch every collection
    const fetches = COLLECTIONS_TO_SYNC.map(async ([cloudColl, localTable]) => {
      const ref = collection(db, 'companies', companyId, cloudColl);
      const snap = await getDocs(ref);
      recordReads(companyId, snap.size, 'pos-master-sync');
      const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      await cacheCollection(localTable, companyId, rows);
      counts[localTable] = rows.length;
    });

    // Also pull two settings docs POS needs
    fetches.push((async () => {
      try {
        const numbering = await getDoc(doc(db, 'companies', companyId, 'settings', 'documentNumbering'));
        const formats = await getDoc(doc(db, 'companies', companyId, 'settings', 'documentFormats'));
        const settingsRows = [];
        if (numbering.exists()) settingsRows.push({ id: 'documentNumbering', ...numbering.data() });
        if (formats.exists()) settingsRows.push({ id: 'documentFormats', ...formats.data() });
        // Reuse the `company` table for single-row settings (keyed by id)
        // Actually we don't have a settings table yet — store inline on company doc
        // For now just skip; POS works fine without them (uses tranType defaults)
        counts.settings = settingsRows.length;
      } catch (e) { /* non-fatal */ }
    })());

    // Also cache the active company doc itself
    fetches.push((async () => {
      try {
        const companyDoc = await getDoc(doc(db, 'companies', companyId));
        if (companyDoc.exists()) {
          await cacheCollection('company', companyId, [{ id: companyDoc.id, ...companyDoc.data() }]);
          counts.company = 1;
        }
      } catch (e) { /* non-fatal */ }
    })());

    await Promise.all(fetches);

    _lastSyncAt = new Date().toISOString();
    try { localStorage.setItem(`amal_pos_sync_${companyId}`, String(Date.now())); } catch { /* ignore */ }
    const durationMs = Date.now() - t0;
    notify({ phase: 'completed', finishedAt: _lastSyncAt, durationMs, counts });
    return { ok: true, durationMs, counts };
  } catch (error) {
    const durationMs = Date.now() - t0;
    console.error('[posMasterSync] failed:', error);
    notify({ phase: 'error', error, durationMs });
    return { ok: false, durationMs, counts, error };
  } finally {
    _syncInFlight = false;
  }
};

/**
 * Estimate how stale the local cache is, in minutes. null if never synced.
 */
export const getMinutesSinceLastSync = () => {
  if (!_lastSyncAt) return null;
  return Math.floor((Date.now() - new Date(_lastSyncAt).getTime()) / 60000);
};
